<script setup lang="ts">
  import {
    Button,
    ButtonAppearance,
    ButtonSize,
    Picture,
    PictureFormat,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import BannerDecor from './BannerDecor.vue';
  import { GIFT_CERTIFICATES_LINK } from '../config/giftCertificates';
  import { SERP_GIFT_CERTIFICATES_BANNER_TEXTS } from '../config/texts';

  interface GiftCertificatesBannerProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
  }

  defineProps<GiftCertificatesBannerProps>();

  const open = (): void => {
    window.open(GIFT_CERTIFICATES_LINK, '_blank', 'noopener');
  };
</script>

<template>
  <section
    class="gift-certificates-banner"
    data-testid="gift-certificates-banner"
    @click="open"
  >
    <Picture
      class="gift-certificates-banner__picture"
      path="illustrations/cert-travel.webp"
      :extensions="[PictureFormat.WEBP]"
      :high-resolution="true"
      fit="contain"
      aria-hidden="true"
      data-testid="gift-certificates-banner-picture"
    />

    <div class="gift-certificates-banner__card">
      <span
        class="gift-certificates-banner__glow"
        aria-hidden="true"
      />
      <BannerDecor variant="gift-certificates" />

      <div class="gift-certificates-banner__content">
        <div class="gift-certificates-banner__text">
          <Typo
            :variant="TypoVariant.Heading5"
            tag="p"
          >
            {{ SERP_GIFT_CERTIFICATES_BANNER_TEXTS.title }}
          </Typo>
          <Typo
            class="gift-certificates-banner__subtitle"
            :variant="TypoVariant.BodyMRegular"
            tag="p"
          >
            {{ SERP_GIFT_CERTIFICATES_BANNER_TEXTS.subtitle }}
          </Typo>
        </div>

        <Button
          class="gift-certificates-banner__button"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.M"
          data-testid="gift-certificates-banner-button"
        >
          {{ SERP_GIFT_CERTIFICATES_BANNER_TEXTS.button }}
        </Button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  $picture-width: 327px;
  $picture-height: 184px;
  $picture-space: 78px;

  .gift-certificates-banner {
    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    isolation: isolate;

    &__picture {
      height: $picture-height;
      pointer-events: none;
      position: relative;
      width: $picture-width;
      z-index: 1;
    }

    &__card {
      align-self: stretch;
      background: linear-gradient(233deg, rgb(34 204 238) 0%, rgb(33 139 255) 73%, rgb(9 105 218) 100%);
      border-radius: var(--rounding-xl);
      box-sizing: border-box;
      color: var(--color-text-inverse);
      margin-top: calc(#{$picture-space} + var(--spacing-m) - #{$picture-height});
      overflow: hidden;
      padding: calc(#{$picture-space} + var(--spacing-m) * 2) var(--spacing-m) var(--spacing-m);
      position: relative;
    }

    &__glow {
      background: rgb(201 233 244 / 0.8);
      border-radius: 50%;
      filter: blur(52px);
      height: 104px;
      left: -15px;
      opacity: 0.47;
      pointer-events: none;
      position: absolute;
      top: 13px;
      width: 389px;
    }

    &__content {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      position: relative;
      text-align: center;
    }

    &__text {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__subtitle {
      max-width: 261px;
    }

    &__button {
      border: none;
    }
  }
</style>
